import { useQuery } from '@apollo/client';
import * as inflection from 'inflection';
import * as _ from 'lodash-es';
import React, { useMemo, useState } from 'react';
import TemplatesSuggestions from './TemplatesSuggestions';
import * as styles from './TemplatesView.module.scss';
import PageLayout from '../app/layout/PageLayout';
import TwoColumnRow from '../app/layout/TwoColumnRow';
import useAppSelector, { useAppDispatch } from '../app/redux/useAppSelector';
import CollapsibleButton from '../components/CollapsibleButton';
import EmptyState from '../components/EmptyState';
import PageTitle from '../components/PageTitle';
import Timestamp from '../components/Timestamp';
import type { DocumentType } from '../graphql/generated/gql';
import { gql } from '../graphql/generated/gql';
import { PostSort } from '../graphql/generated/graphql';
import topicsIcon from '../icons/non-prefilled/16x16/allTopics.svg';
import memberIcon from '../icons/non-prefilled/16x16/member.svg';
import orderIcon from '../icons/non-prefilled/16x16/order.svg';
import unorganizedIcon from '../icons/non-prefilled/16x16/unorganized.svg';
import PostFilterMenu from '../post/PostFilterMenu';
import { TopicPostSectionSimpleList } from '../topic/posts/TopicPostSection';
import TopicBreadcrumbs from '../topic/TopicBreadcrumbs';
import TopicNameWithIcon from '../topic/TopicNameWithIcon';
import { useAllTopicMap } from '../topic/useAllTopics';
import { UIActions } from '../ui/uiSlice';
import ConnectionHelper from '../utils/graphql/ConnectionHelper';

const ALLOWED_SORT_OPTIONS = [
  PostSort.Popularity,
  PostSort.Published,
  PostSort.Alphabetical,
  PostSort.Edited,
  PostSort.Viewed,
] as const;

type AllowedSortOptions = Extract<
  PostSort,
  (typeof ALLOWED_SORT_OPTIONS)[number]
>;

const isSortOptionAllowed = (sortBy: PostSort): sortBy is AllowedSortOptions =>
  ALLOWED_SORT_OPTIONS.includes(sortBy as AllowedSortOptions);

const TEMPLATES_VIEW_ALL_QUERY = gql(/** @complexity 72 */ `
  query TemplatesViewAll($first: Int = 100, $afterCursor: String, $sortBy: PostSort) {
    listPosts(filters: [TEMPLATE, UNARCHIVED, PUBLISHED], sortBy: $sortBy, first: $first, after: $afterCursor) {
      edges {
        node {
          id
          owner {
            id
          }
          contributions {
            user {
              id
            }
          }
          topics {
            id
          }
          ...TopicPostSectionSimpleList_post
          ...TemplateMetadata_post
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`);

enum TemplateCategories {
  MINE = 'MINE',
  ALL_TOPICS = 'ALL_TOPICS',
  UNORGANIZED = 'UNORGANIZED',
}

const TEMPLATE_CATEGORIES_LABELS = {
  [TemplateCategories.MINE]: 'Mine',
  [TemplateCategories.ALL_TOPICS]: 'All Topics',
  [TemplateCategories.UNORGANIZED]: 'Unorganized',
} as const;

const TEMPLATE_METADATA_ATTRIBUTE: Record<
  AllowedSortOptions,
  'popularity' | 'publishedAt' | 'editedAt' | 'userViewedAt'
> = {
  [PostSort.Popularity]: 'popularity',
  [PostSort.Published]: 'publishedAt',
  [PostSort.Alphabetical]: 'publishedAt',
  [PostSort.Edited]: 'editedAt',
  [PostSort.Viewed]: 'userViewedAt',
};

const TEMPLATE_METADATA_FRAGMENT = gql(`
  fragment TemplateMetadata_post on Post {
    id
    views
    publishedAt
    editedAt
    userViewedAt
  }
`);

interface TemplateMetadataProps {
  post: DocumentType<typeof TEMPLATE_METADATA_FRAGMENT>;
  sortBy: PostSort;
}

const TemplateMetadata = ({ post, sortBy }: TemplateMetadataProps) => {
  const { views, publishedAt, editedAt, userViewedAt } = post;

  if (!isSortOptionAllowed(sortBy)) {
    return null;
  }

  const attribute = TEMPLATE_METADATA_ATTRIBUTE[sortBy];
  switch (attribute) {
    case 'popularity':
      return `${views} ${inflection.inflect('view', views)}`;
    case 'publishedAt':
      return publishedAt && <Timestamp date={publishedAt} />;
    case 'editedAt':
      return editedAt && <Timestamp date={editedAt} />;
    case 'userViewedAt':
      return userViewedAt && <Timestamp date={userViewedAt} />;
    default:
      attribute satisfies never;
      return null;
  }
};

const sortTopics = (
  topicIds: string[],
  topicMap: ReturnType<typeof useAllTopicMap>,
) =>
  _.compact(
    topicIds.map((topicId) => {
      const topic = topicMap[topicId];
      // To each topic assign a path that consists of the topic name and all its ancestors
      return topic
        ? {
            topicId,
            path: [
              topic.name,
              ...topic.ancestors.map((ancestor) => ancestor.name),
            ].toReversed(), // From the highest ancestor to the topic itself
          }
        : null;
    }),
  )
    .toSorted(
      (a, b) =>
        a.path.length !== b.path.length
          ? a.path.length - b.path.length // Prefer higher level (fewer ancestors) topics
          : a.path.join('').localeCompare(b.path.join('')), // Same level sort alphabetically
    )
    .map((item) => item.topicId);

const PAGE_COUNT = 100; // This is large because frontend filters will be applied

interface TemplatesViewProps {
  initialCategory?: TemplateCategories;
}

const TemplatesView = (props: TemplatesViewProps) => {
  const currentUser = useAppSelector((state) => state.session.user);
  const topicMap = useAllTopicMap();
  const dispatch = useAppDispatch();
  const [category, setCategory] = useState<TemplateCategories>(
    props.initialCategory ?? TemplateCategories.ALL_TOPICS,
  );
  const sortBy: AllowedSortOptions = useAppSelector((state) =>
    isSortOptionAllowed(state.ui.templatesOrder)
      ? state.ui.templatesOrder
      : PostSort.Popularity,
  );

  const templatesResponse = useQuery(TEMPLATES_VIEW_ALL_QUERY, {
    variables: { first: PAGE_COUNT, sortBy },
  });

  const { data: allTemplatesData } = templatesResponse;

  const fetchMore = ConnectionHelper.generateFetchMore(
    TEMPLATES_VIEW_ALL_QUERY,
    templatesResponse,
    'listPosts',
  );

  const allTemplates = _.compact(
    allTemplatesData?.listPosts?.edges?.map((edge) => edge?.node),
  );

  const { mine, unorganized, allTopics, current, topicIds } = useMemo(() => {
    const [withTopics, withoutTopics] = _.partition(
      allTemplates,
      (template) => template.topics.length > 0,
    );

    // For templates that are not in a topic:
    // 1. if they are mine, they are mine ;-)
    // 2. if they are not mine, they are unorganized
    const [mineTemplates, unorganizedTemplates] = _.partition(
      withoutTopics,
      (template) =>
        template.owner?.id === currentUser.id ||
        template.contributions.some(({ user }) => user.id === currentUser.id),
    );

    let currentTemplates: typeof withTopics = [];

    if (category === TemplateCategories.MINE) {
      currentTemplates = mineTemplates;
    } else if (category === TemplateCategories.ALL_TOPICS) {
      currentTemplates = withTopics;
    } else if (category === TemplateCategories.UNORGANIZED) {
      currentTemplates = unorganizedTemplates;
    }

    const topicIdsWithTemplates = _.uniq(
      currentTemplates.flatMap((template) => template.topics.map((t) => t.id)),
    );

    return {
      mine: mineTemplates.length,
      unorganized: unorganizedTemplates.length,
      allTopics: withTopics.length,
      current: currentTemplates,
      topicIds: sortTopics(topicIdsWithTemplates, topicMap),
    };
  }, [allTemplates, category, currentUser.id, topicMap]);

  const canShowPostCounts = !fetchMore;

  const controls = useMemo(() => {
    const categoriesOptions = {
      [TemplateCategories.MINE]: {
        label: TEMPLATE_CATEGORIES_LABELS[TemplateCategories.MINE],
        icon: memberIcon,
        count: mine,
      },
      [TemplateCategories.ALL_TOPICS]: {
        label: TEMPLATE_CATEGORIES_LABELS[TemplateCategories.ALL_TOPICS],
        icon: topicsIcon,
        count: allTopics,
      },
      [TemplateCategories.UNORGANIZED]: {
        label: TEMPLATE_CATEGORIES_LABELS[TemplateCategories.UNORGANIZED],
        icon: unorganizedIcon,
        count: unorganized,
      },
    };

    return (
      <>
        <PostFilterMenu<TemplateCategories>
          onChange={setCategory}
          options={
            canShowPostCounts
              ? categoriesOptions
              : _.mapValues(categoriesOptions, (option) => ({
                  ...option,
                  count: undefined,
                }))
          }
          value={category}
          title="Show"
        />
        <PostFilterMenu<AllowedSortOptions>
          icon={orderIcon}
          onChange={(order) => {
            dispatch(UIActions.changeTemplatesOrder(order));
          }}
          options={{
            [PostSort.Popularity]: { label: 'Popularity' },
            [PostSort.Published]: { label: 'Recently published' },
            [PostSort.Edited]: { label: 'Recently edited' },
            [PostSort.Viewed]: { label: 'Recently viewed' },
            [PostSort.Alphabetical]: { label: 'Alphabetical' },
          }}
          value={sortBy}
          title="Sort"
        />
      </>
    );
  }, [
    mine,
    allTopics,
    unorganized,
    category,
    sortBy,
    canShowPostCounts,
    dispatch,
  ]);

  return (
    <PageLayout.Content noPaddingX noPaddingY>
      <div className={styles.container}>
        <TwoColumnRow
          full={
            <>
              <PageTitle title="Templates" />
              {topicIds.length === 0 ? (
                <TopicPostSectionSimpleList
                  renderMeta={(post) => (
                    <TemplateMetadata post={post} sortBy={sortBy} />
                  )}
                  title={TEMPLATE_CATEGORIES_LABELS[category]}
                  posts={current}
                  controls={controls}
                />
              ) : (
                topicIds.map((topicId, index) => {
                  const topic = topicMap[topicId];
                  if (!topic) {
                    return null;
                  }
                  return (
                    <TopicPostSectionSimpleList
                      key={topicId}
                      aboveTitle={
                        <TopicBreadcrumbs
                          link
                          topics={topic.ancestors}
                          size="small"
                          renderName={(name, ancestor) => (
                            <TopicNameWithIcon name={name} topic={ancestor} />
                          )}
                        />
                      }
                      title={
                        <TopicNameWithIcon topic={topic} iconSize="medium" />
                      }
                      posts={current.filter((template) =>
                        template.topics.some((t) => t.id === topicId),
                      )}
                      controls={index === 0 ? controls : null}
                      renderMeta={(post) => (
                        <TemplateMetadata post={post} sortBy={sortBy} />
                      )}
                    />
                  );
                })
              )}
              {!!fetchMore && (
                // Use a button instead of an InfiniteScrollWaypoint because
                // frontend filters can result in only a few items per page, so the
                // InfiniteScrollWaypoint may always be visible in the viewport and
                // not trigger automatic loading.
                <CollapsibleButton onClick={() => fetchMore()} collapsed>
                  Load more
                </CollapsibleButton>
              )}
              {!templatesResponse.loading && (
                <>
                  {!current.length && (
                    <div className={styles.empty}>
                      <EmptyState imageName="templates" vertical noMargin />
                    </div>
                  )}
                  <TemplatesSuggestions />
                </>
              )}
            </>
          }
        />
      </div>
    </PageLayout.Content>
  );
};

export default TemplatesView;
